// The (Primary × Weekday) matrix table. The primary axis is the active grouping
// dimension (doctor or tenant). Each body row is one (primary, secondary) lane;
// a primary entity with multiple secondaries spans several rows via `rowSpan`
// so the primary card renders once. Every weekday cell is a clickable mini
// day-plan.
//
// `mode` ("definitions" | "available") only affects copy; `groupBy`
// ("doctor" | "tenant") flips which dimension is primary vs. lane.

import { useMemo } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { resolveTenantDisplayName } from "@/lib/tenant-display";
import { DayColumn, HourRulerMini } from "./day-column";
import { HueAvatar, Tag } from "./heatmap-bits";
import { getVisibleWeekdayColumns, hashHue, slotWeekdays } from "./heatmap-utils";

// Read the lane-axis identity off a row (the opposite dimension from the
// primary grouping).
function laneIdOf(row, groupBy) {
  return groupBy === "tenant" ? row?.doctorId : row?.tenantId;
}
function laneNameOf(row, groupBy) {
  if (groupBy === "tenant") return row?.doctor || row?.doctorShort || "Doctor";
  return row?.tenantName || resolveTenantDisplayName(row?._tenant) || "Clinic";
}

// The weekday cells + the per-lane total cell for one (primary, lane) row.
function LaneCells({ groupKey, lane, rows, groupBy, scale, activeCell, onSelectCell, columns }) {
  // Bucket this lane's rows by weekday once, instead of 7 filters per render.
  const { byWeekday, total } = useMemo(() => {
    const laneRows = rows.filter((row) => laneIdOf(row, groupBy) === lane.id);
    const buckets = new Map();
    let sum = 0;
    for (const row of laneRows) {
      const days = slotWeekdays(row);
      sum += days.length;
      for (const day of days) {
        if (!buckets.has(day)) buckets.set(day, []);
        buckets.get(day).push(row);
      }
    }
    return { byWeekday: buckets, total: sum };
  }, [rows, lane.id, groupBy]);

  return (
    <>
      {columns.map((col, idx) => {
        const weekday = col.iso;
        const dayRows = byWeekday.get(weekday) || [];
        const isActive =
          activeCell?.groupKey === groupKey && activeCell?.weekday === weekday;
        const isWeekend = col.weekend;
        return (
          <td
            key={idx}
            className={cn(
              "border-b border-border p-0 align-top",
              isWeekend && "bg-black/[0.04] dark:bg-background/40",
            )}
          >
            <button
              type="button"
              onClick={() => onSelectCell({ groupKey, weekday })}
              className={cn(
                "group relative block h-[78px] w-full p-1 text-left transition-colors",
                isActive ? "bg-primary/10" : "hover:bg-muted/40",
              )}
            >
              {/* The lane hue colors uncolored bands — tenant hue when lanes
                  are tenants, doctor hue when lanes are doctors. */}
              <DayColumn rows={dayRows} tenantHue={lane.hue} scale={scale} />
              {isActive ? (
                <span className="pointer-events-none absolute inset-0 rounded-[2px] ring-2 ring-inset ring-primary/70" />
              ) : null}
            </button>
          </td>
        );
      })}
      <td className="border-b border-border px-2 align-middle">
        <div className="flex items-center justify-end gap-1.5">
          <Tag id={lane.id}>{lane.name}</Tag>
          <span className="font-mono text-[10px] tabular-nums text-foreground/80">
            {total}×
          </span>
        </div>
      </td>
    </>
  );
}

// One primary entity: a card cell spanning all of its lanes, then a lane row per
// secondary entity. An entity whose rows were filtered away still shows the
// card with blank lanes.
function GroupRows({ group, scale, mode, groupBy, activeCell, onSelectCell, columns }) {
  const rows = group.rows;

  const lanes = useMemo(() => {
    const ids = Array.from(
      new Set(rows.map((row) => laneIdOf(row, groupBy)).filter(Boolean)),
    );
    return ids.map((id) => {
      const sample = rows.find((row) => laneIdOf(row, groupBy) === id);
      return {
        id,
        name: laneNameOf(sample, groupBy),
        hue: hashHue(id),
      };
    });
  }, [rows, groupBy]);

  const occurrences = useMemo(
    () => rows.reduce((acc, row) => acc + (row.occurrenceInRange || 1), 0),
    [rows],
  );
  const rowSpan = lanes.length || 1;
  const metaLine =
    mode === "available"
      ? `${rows.length} free`
      : `${rows.length} defs · ${occurrences}×/mo`;

  const primaryCell = (
    <td rowSpan={rowSpan} className="border-b border-border bg-muted/30 px-4 py-2 align-top">
      <div className="flex items-center gap-2">
        <HueAvatar name={group.label} id={group.key} size={26} />
        <div className="min-w-0">
          <div className="truncate text-xs font-semibold text-foreground">{group.label}</div>
          <div className="text-[10px] tabular-nums text-muted-foreground/60">{metaLine}</div>
        </div>
      </div>
    </td>
  );

  if (lanes.length === 0) {
    return (
      <tr>
        {primaryCell}
        {columns.map((col, idx) => (
          <td key={idx} className="border-b border-border bg-muted/20" />
        ))}
        <td className="border-b border-border bg-muted/20" />
      </tr>
    );
  }

  return (
    <>
      <tr>
        {primaryCell}
        <LaneCells
          groupKey={group.key}
          lane={lanes[0]}
          rows={rows}
          groupBy={groupBy}
          scale={scale}
          activeCell={activeCell}
          onSelectCell={onSelectCell}
          columns={columns}
        />
      </tr>
      {lanes.slice(1).map((lane) => (
        <tr key={lane.id}>
          <LaneCells
            groupKey={group.key}
            lane={lane}
            rows={rows}
            groupBy={groupBy}
            scale={scale}
            activeCell={activeCell}
            onSelectCell={onSelectCell}
            columns={columns}
          />
        </tr>
      ))}
    </>
  );
}

export function HeatmapMatrix({
  groups = [],
  weekDates = [],
  scale,
  onToggleScale,
  mode = "definitions",
  groupBy = "doctor",
  activeCell,
  onSelectCell,
  isLoading,
  hideSunday = false,
}) {
  const isEmpty = groups.length === 0;
  // Header reflects the primary axis first, lane axis second.
  const headerLabel = groupBy === "tenant" ? "Location · Doctor" : "Doctor · Location";
  // The weekday columns actually rendered (Sunday dropped when hidden).
  const columns = useMemo(() => getVisibleWeekdayColumns(hideSunday), [hideSunday]);

  return (
    <div className="relative min-h-0 flex-1">
      <ScrollArea className="h-full">
        <table className="w-full border-separate border-spacing-0 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="w-[200px] border-b border-border bg-card/60 px-4 py-2 text-left backdrop-blur">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
                  {headerLabel}
                </div>
              </th>
              {columns.map((col, idx) => {
                const date = weekDates.find((d) => d?.weekday === col.iso);
                return (
                  <th
                    key={idx}
                    className="border-b border-border bg-card/60 px-2 py-2 backdrop-blur"
                  >
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          "text-[11px] font-bold",
                          col.weekend ? "text-muted-foreground/60" : "text-foreground",
                        )}
                      >
                        {col.letter}
                      </span>
                      <span className="text-[10px] tabular-nums text-muted-foreground">
                        {date?.toFormat?.("d") ?? ""}
                      </span>
                    </div>
                  </th>
                );
              })}
              <th className="w-[64px] border-b border-border bg-card/60 px-2 py-2 text-right backdrop-blur">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
                  Total
                </span>
              </th>
            </tr>
            <tr>
              <td className="border-b border-border bg-muted/40 p-0">
                <button
                  type="button"
                  onClick={onToggleScale}
                  title="Toggle the cell time axis between working hours and a full 24-hour day"
                  className="block w-full px-4 py-1 text-left text-[10px] text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
                >
                  {scale.legend}
                </button>
              </td>
              {columns.map((col, idx) => (
                <td key={idx} className="border-b border-border bg-muted/40 px-2 py-1">
                  <HourRulerMini scale={scale} />
                </td>
              ))}
              <td className="border-b border-border bg-muted/40 px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <GroupRows
                key={group.key}
                group={group}
                scale={scale}
                mode={mode}
                groupBy={groupBy}
                activeCell={activeCell}
                onSelectCell={onSelectCell}
                columns={columns}
              />
            ))}
          </tbody>
        </table>
      </ScrollArea>

      {isLoading && isEmpty ? (
        <div className="absolute inset-0 grid place-items-center bg-background/60">
          <Spinner className="size-6" />
        </div>
      ) : null}
      {!isLoading && isEmpty ? (
        <div className="absolute inset-0 grid place-items-center p-6">
          <div className="rounded-lg border border-dashed border-border bg-muted/15 p-6 text-center">
            <p className="text-sm font-medium text-foreground">
              {mode === "available"
                ? "No free slots this week"
                : "No slot definitions in range"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {mode === "available"
                ? "Every slot is booked, or none are scheduled for this week."
                : "Try widening the search or creating a new slot."}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
